import { ILoadingError } from './useLoadingErrorCatcher.types';
import { allPass, anyPass, complement, equals, pathEq, pathOr, propEq, propSatisfies } from 'ramda';
import { isTruthy } from 'ramda-adjunct';

type TErrorChecker = (error: ILoadingError) => boolean;

export const isNetworkError: TErrorChecker = (error) => {
  if (!error) {
    return false;
  }

  // Check for exact 'Network Error' message (e.g., from axios)
  if (propEq('message', 'Network Error', error)) {
    return true;
  }

  // Check for SDK's no-response error name
  if (propEq('name', 'MediaManagerSDKNoResponseError', error)) {
    return true;
  }

  // Check for code 0 which typically indicates network/connectivity error
  if (propEq('code', 0, error)) {
    return true;
  }

  // Check if message starts with 'Network Error' (handles variations)
  if (error.message && typeof error.message === 'string' && error.message.startsWith('Network Error')) {
    return true;
  }

  return false;
};

export const isNotAuthorizedError: TErrorChecker = (error) =>
  Boolean(error) && propSatisfies(anyPass([equals(401), equals(403)]), 'code', error);

export const isCancelError = (error: ILoadingError): boolean => Boolean(error) && propEq('message', 'cancelled', error);

export const isRecommendedHomeError: TErrorChecker = pathEq(['data', 'source'], 'recommended');

export const isUnknownLoadingError: TErrorChecker = allPass([
  isTruthy,
  complement(anyPass([isNotAuthorizedError, isRecommendedHomeError, isCancelError, isNetworkError])),
]);

export const getErrorMessage = (error: ILoadingError): string =>
  error.message ||
  pathOr<string, string>('', ['data', 'message'], error) ||
  pathOr<string, string>('', ['data', 'error_description'], error) ||
  pathOr<string, string>('', ['data', 'error_info', 'message'], error);
